/** The pure half of the Facilities band - ADR-0212, package P9. Plain data in, plain data out: the
 * coverage grid's rows and columns from `GET /coverage`. Nothing here reads, adds up a figure or
 * rounds one. (The filing page's readiness ring and its "by station" funnel were removed on
 * 2026-11-03: the percentage and the path already say it, and the ring's legend counted a waiting
 * check twice, "Passed 10, Not run 2, Open 4" out of 14.) */

import type { CoverageRollup } from "../../api/coverageRollup";
import type { CoverageSite, CoverageState } from "../../components/charts";
import { monthLabel, shortMonthYear } from "../../lib/format";
import { words } from "../scopeTable/model";

export const GRID_ROWS = 8;
export const GRID_MONTHS = 24;

const STATE_OF: Readonly<Record<string, CoverageState>> = {
  submitted: "received",
  received: "received",
  declared_nil: "declared_nil",
  missing: "missing",
  pending: "pending",
  unknown: "unknown",
  not_expected: "not_expected",
};

/** The months of a window as grid columns, `YYYY-MM`, at most `GRID_MONTHS`. */
export function monthsOf(start: string, end: string): { id: string; label: string; full: string }[] {
  const first = Number(start.slice(0, 4)) * 12 + Number(start.slice(5, 7)) - 1;
  const last = Number(end.slice(0, 4)) * 12 + Number(end.slice(5, 7)) - 1;
  const out: { id: string; label: string; full: string }[] = [];
  for (let at = first; at <= last && out.length < GRID_MONTHS; at += 1) {
    const id = `${String(Math.floor(at / 12))}-${String((at % 12) + 1).padStart(2, "0")}`;
    out.push({ id, label: shortMonthYear(id).split(" ")[0] ?? id, full: monthLabel(id) });
  }
  return out;
}

export interface CoverageGridModel {
  sites: CoverageSite[];
  months: { id: string; label: string; full: string }[];
  /** How many streams the census holds beyond the rows drawn. */
  more: number;
}

export function coverageGridOf(rollup: CoverageRollup): CoverageGridModel {
  const months = monthsOf(rollup.period_start, rollup.period_end);
  const streams = rollup.sites.flatMap((site) => site.sources.map((source) => ({ site, source })));
  const ranked = [...streams].sort(
    (a, b) => b.source.missing - a.source.missing || a.site.facility_name.localeCompare(b.site.facility_name),
  );
  const sites: CoverageSite[] = ranked.slice(0, GRID_ROWS).map(({ site, source }) => ({
    id: source.source_id,
    label: site.facility_name,
    expects: source.label ?? words(source.category),
    cells: months.map((month) => {
      const first = `${month.id}-01`;
      const window = source.windows.find((candidate) => candidate.period_start <= first && first <= candidate.period_end);
      return {
        state: (window ? STATE_OF[window.state] : undefined) ?? "not_expected",
        receipt: source.source_id,
      };
    }),
  }));
  return { sites, months, more: Math.max(0, streams.length - sites.length) };
}


